import React, { memo, startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/useToast";
import {
  deleteResearchDocumentDefault,
  getDocumentsForMunicipality,
  updateChatConfiguration,
  updateResearchDocumentDefault,
} from "@/api/messages";
import { ChevronDown, ChevronRight, FolderOpen } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import {
  allGroupedDocumentIds,
  buildChildrenByParentId,
  documentMatchesSearch,
  documentSearchHaystack,
  documentsInIdOrder,
  groupIsSearchMatch,
  groupMatchesQuery,
  indexGroupsById,
  limitSearchResults,
  subtreeDocumentIds,
} from "@/lib/documentGroupTree";

interface Document {
  id: number;
  name: string;
  description?: string;
  status: string;
}

/** Wait for typing to settle before filtering; every keystroke otherwise re-filters the whole corpus. */
export const SEARCH_DEBOUNCE_MS = 150;
/**
 * Above this many visible matches, a search leaves folders collapsed (showing their match counts)
 * instead of auto-expanding them. Rows are real DOM, and a one-character query on a large corpus
 * can match tens of thousands of documents (ENG-2096).
 */
export const SEARCH_AUTO_EXPAND_LIMIT = 200;

interface DocumentGroup {
  id: number;
  name: string;
  description: string;
  position: number;
  parent_id?: number | null;
  document_ids: number[];
}

/** Shown when a new staff research chat has loaded with nothing selected. */
export const EMPTY_RESEARCH_SELECTION_PROMPT =
  "No documents are selected for this chat. Choose the documents you want answers to use.";

interface DocumentSelectorProps {
  chatId: number;
  municipalityId: number;
  selectedDocuments: number[];
  onSelectionChange?: (selectedDocuments: number[], totalDocuments?: number) => void;
  onDocumentCountLoaded?: (count: number) => void;
  allowSaveAsDefault?: boolean;
  /** Open once for this chat. Dismissing it does not open it again. */
  promptForEmptySelection?: boolean;
}

/** Rendered after a search-limited list so the user knows rows were left unmounted. */
function SearchResultsTruncated({ visibleCount, hiddenCount }: { visibleCount: number; hiddenCount: number }) {
  if (hiddenCount <= 0) return null;
  return (
    <p className="px-1 py-1 text-[11px] leading-snug text-muted-foreground">
      Showing {visibleCount} of {(visibleCount + hiddenCount).toLocaleString()}. Narrow your search to see
      the rest.
    </p>
  );
}

function DocumentSelectorTitle({ name, status }: { name: string; status?: string }) {
  return (
    <span className="block min-w-0 line-clamp-2 break-words font-medium leading-snug">
      {name}
      {status && status !== "completed" ? (
        <span className="ml-1.5 text-xs font-normal italic text-muted-foreground">({status})</span>
      ) : null}
    </span>
  );
}

function searchHitsDescriptionOnly(
  doc: { name: string; description?: string },
  normalizedSearch: string,
): boolean {
  const description = doc.description;
  if (!normalizedSearch || !description?.trim()) return false;
  if (doc.name.toLowerCase().includes(normalizedSearch)) return false;
  return description.toLowerCase().includes(normalizedSearch);
}

const DESCRIPTION_EXCERPT_LENGTH = 80;

function descriptionExcerptAroundMatch(description: string, normalizedSearch: string): string {
  const matchIndex = description.toLowerCase().indexOf(normalizedSearch);
  if (matchIndex < 0 || description.length <= DESCRIPTION_EXCERPT_LENGTH) return description;

  const matchLength = normalizedSearch.length;
  const windowLength = Math.max(DESCRIPTION_EXCERPT_LENGTH, matchLength);
  if (description.length <= windowLength) return description;

  let start = Math.max(0, matchIndex - Math.floor((windowLength - matchLength) / 2));
  let end = Math.min(description.length, start + windowLength);
  if (end - start < windowLength) {
    start = Math.max(0, end - windowLength);
  }
  if (start > matchIndex) start = matchIndex;

  if (start > 0) {
    const boundary = description.indexOf(" ", start);
    if (boundary !== -1 && boundary < matchIndex) start = boundary + 1;
  }
  if (end < description.length) {
    const boundary = description.lastIndexOf(" ", end);
    if (boundary > matchIndex + matchLength) end = boundary;
  }

  const excerpt = description.slice(start, end).trim();
  const prefix = start > 0 ? "…" : "";
  const suffix = end < description.length ? "…" : "";
  return `${prefix}${excerpt}${suffix}`;
}

function DocumentSearchMatchDescription({
  description,
  normalizedSearch,
}: {
  description: string;
  normalizedSearch: string;
}) {
  return (
    <span className="mt-0.5 block line-clamp-1 break-words text-xs leading-snug text-muted-foreground">
      {descriptionExcerptAroundMatch(description, normalizedSearch)}
    </span>
  );
}

function DocumentRowDescription({
  doc,
  normalizedSearch,
  showSearchMatchedDescription,
}: {
  doc: { name: string; description?: string };
  normalizedSearch: string;
  showSearchMatchedDescription: boolean;
}) {
  if (showSearchMatchedDescription) {
    if (!searchHitsDescriptionOnly(doc, normalizedSearch) || !doc.description) return null;
    return (
      <DocumentSearchMatchDescription description={doc.description} normalizedSearch={normalizedSearch} />
    );
  }
  if (!doc.description) return null;
  return <span className="mt-0.5 block text-xs text-muted-foreground">{doc.description}</span>;
}

type DocumentGroupTreeRowsProps = {
  rootGroups: DocumentGroup[];
  depth: number;
  documentsById: Map<number, Document>;
  selected: number[];
  /** Built once by the owner; the selection can be tens of thousands of ids and this tree recurses. */
  selectedSet: Set<number>;
  setSelected: React.Dispatch<React.SetStateAction<number[]>>;
  expandedGroups: Set<number>;
  toggleGroupExpanded: (groupId: number) => void;
  childrenByParent: Map<number | null, DocumentGroup[]>;
  completedSubtreeDocIdsByGroupId: Map<number, number[]>;
  normalizedSearch?: string;
  groupMatchesSearch?: (groupId: number) => boolean;
  revealContents?: boolean;
  searchHaystackById?: Map<number, string>;
  /** Research Assistant rows show a description only when search hits it and misses the title. */
  showSearchMatchedDescription?: boolean;
};

/**
 * Memoized because a search keystroke re-renders the dialog before the debounce settles, and with
 * a folder open that was up to a thousand rows per key (ENG-2096). MunicipalityDocumentPickerPanel
 * defeats the memo with an inline `setSelected` that closes over the current selection; wrapping
 * that arrow in useCallback would make functional updaters resolve against a stale `selected`.
 */
const DocumentGroupTreeRows = memo(function DocumentGroupTreeRows(props: DocumentGroupTreeRowsProps) {
  const {
    rootGroups,
    depth,
    documentsById,
    selected,
    selectedSet,
    setSelected,
    expandedGroups,
    toggleGroupExpanded,
    childrenByParent,
    completedSubtreeDocIdsByGroupId,
    normalizedSearch = "",
    groupMatchesSearch = () => true,
    revealContents = false,
    searchHaystackById,
    showSearchMatchedDescription = false,
  } = props;

  function handleGroupToggle(group: DocumentGroup) {
    const groupDocIds = completedSubtreeDocIdsByGroupId.get(group.id) ?? [];
    const allSelected = groupDocIds.length > 0 && groupDocIds.every((id) => selectedSet.has(id));

    if (allSelected) {
      const toRemove = new Set(groupDocIds);
      setSelected((prev) => prev.filter((id) => !toRemove.has(id)));
    } else {
      setSelected((prev) => {
        const nextSet = new Set(prev);
        for (const id of groupDocIds) {
          nextSet.add(id);
        }
        return Array.from(nextSet);
      });
    }
  }

  const isRootLevel = depth === 0;

  return (
    <div className={cn("min-w-0", isRootLevel ? "space-y-3" : "space-y-1")}>
      {rootGroups.map((group) => {
        if (normalizedSearch && !revealContents && !groupMatchesSearch(group.id)) {
          return null;
        }

        const reveal = revealContents || groupMatchesQuery(group, normalizedSearch);
        const isExpanded = expandedGroups.has(group.id);
        // Only an open folder mounts document rows, so only an open folder pays to filter them.
        // During a search the mounted rows are capped; Select All still uses the full match set.
        const directDocs = isExpanded
          ? documentsInIdOrder(documentsById, group.document_ids).filter(
              (doc) => reveal || documentMatchesSearch(doc, normalizedSearch, searchHaystackById?.get(doc.id)),
            )
          : [];
        const limitedDirectDocs = normalizedSearch
          ? limitSearchResults(directDocs)
          : { visible: directDocs, hiddenCount: 0 };
        const groupDocIds = completedSubtreeDocIdsByGroupId.get(group.id) ?? [];
        const completedSubtreeCount = groupDocIds.length;
        const fullySelected =
          groupDocIds.length > 0 && groupDocIds.every((id) => selectedSet.has(id));
        const selectedCount = groupDocIds.reduce((n, id) => n + (selectedSet.has(id) ? 1 : 0), 0);
        const partiallySelected = selectedCount > 0 && selectedCount < groupDocIds.length;
        const childGroups = childrenByParent.get(group.id) ?? [];

        return (
          <Collapsible
            key={group.id}
            open={isExpanded}
            onOpenChange={() => toggleGroupExpanded(group.id)}
            className={cn(
              isRootLevel &&
                "w-full min-w-0 max-w-full rounded-lg border border-border/50 bg-card px-2 py-2 shadow-none",
            )}
          >
            <div className="min-w-0 rounded-md py-0.5">
              <div className="flex min-w-0 items-start gap-2">
                <Checkbox
                  checked={fullySelected}
                  aria-label={`Select all documents in ${group.name}`}
                  className={cn(
                    "mt-0.5 shrink-0",
                    partiallySelected && "data-[state=checked]:bg-blue-500",
                  )}
                  onCheckedChange={() => handleGroupToggle(group)}
                  disabled={completedSubtreeCount === 0}
                />
                <CollapsibleTrigger className="flex min-w-0 flex-1 items-start gap-2 rounded-md py-0.5 text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <FolderOpen
                    className={cn(
                      "mt-0.5 shrink-0 text-muted-foreground",
                      isRootLevel ? "h-4 w-4" : "h-3.5 w-3.5",
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <div
                      className={cn(
                        "break-words font-medium leading-snug",
                        isRootLevel ? "text-sm" : "text-[13px]",
                      )}
                    >
                      {group.name}
                    </div>
                    <div className="text-[11px] text-muted-foreground leading-tight">
                      {completedSubtreeCount} document{completedSubtreeCount !== 1 ? "s" : ""}
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                </CollapsibleTrigger>
              </div>

              <CollapsibleContent>
                {isExpanded && (childGroups.length > 0 || directDocs.length > 0) && (
                  <div className="mt-1 min-w-0 space-y-0.5 border-l border-border/45 pl-3 ml-6">
                    {childGroups.length > 0 && (
                      <DocumentGroupTreeRows
                        rootGroups={childGroups}
                        depth={depth + 1}
                        documentsById={documentsById}
                        selected={selected}
                        selectedSet={selectedSet}
                        setSelected={setSelected}
                        expandedGroups={expandedGroups}
                        toggleGroupExpanded={toggleGroupExpanded}
                        childrenByParent={childrenByParent}
                        completedSubtreeDocIdsByGroupId={completedSubtreeDocIdsByGroupId}
                        normalizedSearch={normalizedSearch}
                        groupMatchesSearch={groupMatchesSearch}
                        revealContents={reveal}
                        searchHaystackById={searchHaystackById}
                        showSearchMatchedDescription={showSearchMatchedDescription}
                      />
                    )}
                    {limitedDirectDocs.visible.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex min-w-0 items-start gap-2 rounded-md py-1 pr-1 hover:bg-muted/40"
                      >
                        <Checkbox
                          id={`doc-${doc.id}`}
                          className="mt-0.5 shrink-0"
                          checked={selectedSet.has(doc.id)}
                          disabled={doc.status !== "completed"}
                          onCheckedChange={(checked) => {
                            if (doc.status !== "completed") return;
                            if (checked === true) {
                              setSelected((prev) => (prev.includes(doc.id) ? prev : [...prev, doc.id]));
                            } else {
                              setSelected((prev) => prev.filter((id) => id !== doc.id));
                            }
                          }}
                        />
                        <Label
                          htmlFor={`doc-${doc.id}`}
                          className={cn(
                            "block min-w-0 flex-1 cursor-pointer text-[13px] leading-snug",
                            doc.status !== "completed" && "opacity-60",
                          )}
                        >
                          <DocumentSelectorTitle name={doc.name} status={doc.status} />
                          <DocumentRowDescription
                            doc={doc}
                            normalizedSearch={normalizedSearch}
                            showSearchMatchedDescription={showSearchMatchedDescription}
                          />
                        </Label>
                      </div>
                    ))}
                    <SearchResultsTruncated
                      visibleCount={limitedDirectDocs.visible.length}
                      hiddenCount={limitedDirectDocs.hiddenCount}
                    />
                  </div>
                )}
              </CollapsibleContent>
            </div>
          </Collapsible>
        );
      })}
    </div>
  );
});

type UngroupedDocumentRowsProps = {
  documents: Document[];
  hiddenCount: number;
  selectedSet: Set<number>;
  normalizedSearch: string;
  onCheckedChange: (documentId: number, checked: boolean, status: string) => void;
};

const UngroupedDocumentRows = memo(function UngroupedDocumentRows({
  documents,
  hiddenCount,
  selectedSet,
  normalizedSearch,
  onCheckedChange,
}: UngroupedDocumentRowsProps) {
  return (
    <div className="min-w-0 space-y-2">
      {documents.map((doc) => (
        <div
          key={doc.id}
          className="flex min-w-0 items-start gap-2 rounded-md py-1 hover:bg-muted/40"
        >
          <Checkbox
            id={`doc-${doc.id}`}
            className="mt-0.5 shrink-0"
            checked={selectedSet.has(doc.id)}
            disabled={doc.status !== "completed"}
            onCheckedChange={(checked) => onCheckedChange(doc.id, checked === true, doc.status)}
          />
          <Label
            htmlFor={`doc-${doc.id}`}
            className={cn(
              "block min-w-0 flex-1 cursor-pointer",
              doc.status !== "completed" && "opacity-60",
            )}
          >
            <div className="min-w-0">
              <DocumentSelectorTitle name={doc.name} status={doc.status} />
              <DocumentRowDescription
                doc={doc}
                normalizedSearch={normalizedSearch}
                showSearchMatchedDescription
              />
            </div>
          </Label>
        </div>
      ))}
      <SearchResultsTruncated visibleCount={documents.length} hiddenCount={hiddenCount} />
    </div>
  );
});

export function DocumentSelector({ 
  chatId, 
  municipalityId,
  selectedDocuments = [],
  onSelectionChange,
  onDocumentCountLoaded,
  allowSaveAsDefault = false,
  promptForEmptySelection = false,
}: DocumentSelectorProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [groups, setGroups] = useState<DocumentGroup[]>([]);
  // Folders the user opened by hand. What is actually expanded on screen is derived below from this
  // plus the active search, so a query change and its expansion change always land in one render.
  const [userExpandedGroups, setUserExpandedGroups] = useState<Set<number>>(new Set());
  // Folders the user closed while a search was active; the search must not reopen them on the next
  // keystroke. Cleared when the search clears.
  const [collapsedDuringSearch, setCollapsedDuringSearch] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const promptedChatIdsRef = useRef<Set<number>>(new Set());
  const chatIdRef = useRef(chatId);
  const [selected, setSelected] = useState<number[]>(selectedDocuments);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const { toast } = useToast();

  // Typing waits for the debounce; clearing the box skips it. Both commit the settled query in a
  // transition, so the filter and the resulting tree render never block the keystroke that follows.
  // Until the transition lands, `searchPending` keeps the bulk actions disabled.
  useEffect(() => {
    if (searchQuery === "") {
      startTransition(() => setDebouncedSearchQuery(""));
      return;
    }
    const handle = setTimeout(
      () => startTransition(() => setDebouncedSearchQuery(searchQuery)),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
  }, [searchQuery]);

  const resetSearchState = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setCollapsedDuringSearch(new Set());
  };

  useEffect(() => {
    // Only fetch if the dialog is open and we have a valid municipality ID
    if (open && municipalityId && municipalityId > 0) {
      fetchDocuments();
    }
  }, [open, municipalityId]);
  
  // Fetch count of available documents once when component mounts
  useEffect(() => {
    if (municipalityId && municipalityId > 0) {
      fetchDocuments(false); // Pass false to indicate we're not rendering yet
    }
  }, [municipalityId]);

  useEffect(() => {
    setSelected(selectedDocuments);
  }, [selectedDocuments]);

  useEffect(() => {
    if (chatIdRef.current !== chatId) {
      chatIdRef.current = chatId;
      setOpen(false);
    }
  }, [chatId]);

  useEffect(() => {
    if (!promptForEmptySelection || promptedChatIdsRef.current.has(chatId)) return;
    promptedChatIdsRef.current.add(chatId);
    setOpen(true);
  }, [promptForEmptySelection, chatId]);

  const fetchDocuments = async (showUI = true) => {
    if (showUI) {
      setLoading(true);
    }
    
    try {
      const result = await getDocumentsForMunicipality(municipalityId);
      
      // Handle both new format {documents: [...], groups: [...]} and old format [...]
      let docs: Document[];
      let documentGroups: DocumentGroup[];
      
      if (Array.isArray(result)) {
        // Old format - just an array of documents
        docs = result;
        documentGroups = [];
      } else if (result && typeof result === 'object') {
        // New format - object with documents and groups
        docs = result.documents || [];
        // Hide folders with nothing actionable: no matching doc, or only non-completed research docs
        const rawGroups = (result.groups || []) as DocumentGroup[];
        const preById = indexGroupsById(rawGroups);
        const preChildren = buildChildrenByParentId(rawGroups);
        const preMemo = new Map<number, number[]>();
        documentGroups = rawGroups.filter((group) => {
          const subtree = subtreeDocumentIds(group.id, preById, preChildren, preMemo);
          return subtree.some((id: number) => {
            const doc = docs.find((d) => d.id === id);
            return doc !== undefined && doc.status === "completed";
          });
        });
      } else {
        docs = [];
        documentGroups = [];
      }
      
      setDocuments(docs);
      setGroups(documentGroups);

      // Groups are collapsed by default
      setCollapsedDuringSearch(new Set());
      setUserExpandedGroups(new Set());
      
      // Report only completed documents count to the parent component
      if (onDocumentCountLoaded) {
        const completedDocsCount = docs.filter(doc => doc.status === 'completed').length;
        onDocumentCountLoaded(completedDocsCount);
      }
      
      if (docs.length === 0 && showUI) {
        toast({
          title: "No documents found",
          description: "This municipality doesn't have any documents yet.",
        });
      }
      
      if (showUI) {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error in document fetching process:", error);
      if (showUI) {
        toast({
          title: "Failed to load documents",
          description: "Please try again later",
        });
      }
    } finally {
      if (showUI) {
        setLoading(false);
      }
    }
  };

  const handleCheckedChange = useCallback((documentId: number, checked: boolean, status: string) => {
    // Only process changes for completed documents
    if (status !== "completed") return;

    if (checked) {
      setSelected((prev) => (prev.includes(documentId) ? prev : [...prev, documentId]));
    } else {
      setSelected((prev) => prev.filter((id) => id !== documentId));
    }
  }, []);

  const { groupsById, childrenByParent, rootGroups } = useMemo(() => {
    const byId = indexGroupsById(groups);
    const ch = buildChildrenByParentId(groups);
    return {
      groupsById: byId,
      childrenByParent: ch,
      rootGroups: ch.get(null) ?? [],
    };
  }, [groups]);

  const groupedDocIds = useMemo(() => {
    if (groups.length === 0) return new Set<number>();
    return allGroupedDocumentIds(groups, groupsById, childrenByParent);
  }, [groups, groupsById, childrenByParent]);

  const normalizedSearch = debouncedSearchQuery.trim().toLowerCase();
  // True while the box shows text the debounce has not applied yet. Bulk actions are held back in
  // that window so they cannot act on the previous query's match set.
  const searchPending = searchQuery.trim().toLowerCase() !== normalizedSearch;
  const documentsById = useMemo(() => new Map(documents.map((doc) => [doc.id, doc])), [documents]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const searchHaystackById = useMemo(
    () => new Map(documents.map((doc) => [doc.id, documentSearchHaystack(doc)])),
    [documents],
  );

  const groupMatchesSearch = useMemo(() => {
    const cache = new Map<number, boolean>();
    return (groupId: number) =>
      groupIsSearchMatch(
        groupId,
        normalizedSearch,
        groupsById,
        childrenByParent,
        documentsById,
        cache,
        searchHaystackById,
      );
  }, [childrenByParent, documentsById, groupsById, normalizedSearch, searchHaystackById]);

  const completedSubtreeDocIdsByGroupId = useMemo(() => {
    if (groups.length === 0) return new Map<number, number[]>();
    const completedIds = new Set(
      documents.filter((d) => d.status === "completed").map((d) => d.id),
    );
    const out = new Map<number, number[]>();
    const collect = (groupId: number, ancestorReveal: boolean): number[] => {
      const group = groupsById.get(groupId);
      if (!group) return [];
      const reveal = !normalizedSearch || ancestorReveal || groupMatchesQuery(group, normalizedSearch);
      const direct: number[] = [];
      for (const id of group.document_ids) {
        if (!completedIds.has(id)) continue;
        const doc = documentsById.get(id);
        if (!doc) continue;
        if (reveal || documentMatchesSearch(doc, normalizedSearch, searchHaystackById.get(id))) {
          direct.push(id);
        }
      }
      const nested: number[] = [];
      for (const child of childrenByParent.get(groupId) ?? []) {
        if (normalizedSearch && !reveal && !groupMatchesSearch(child.id)) continue;
        nested.push(...collect(child.id, reveal));
      }
      const ids = [...direct, ...nested];
      out.set(groupId, ids);
      return ids;
    };
    for (const group of rootGroups) {
      collect(group.id, false);
    }
    return out;
  }, [
    childrenByParent,
    documents,
    documentsById,
    groupMatchesSearch,
    groups.length,
    groupsById,
    normalizedSearch,
    rootGroups,
    searchHaystackById,
  ]);

  const ungroupedDocuments = useMemo(
    () =>
      documents.filter(
        (doc) =>
          !groupedDocIds.has(doc.id) &&
          documentMatchesSearch(doc, normalizedSearch, searchHaystackById.get(doc.id)),
      ),
    [documents, groupedDocIds, normalizedSearch, searchHaystackById],
  );
  const ungroupedToRender = useMemo(
    () => (normalizedSearch ? limitSearchResults(ungroupedDocuments) : { visible: ungroupedDocuments, hiddenCount: 0 }),
    [normalizedSearch, ungroupedDocuments],
  );

  const visibleCompletedIds = useMemo(() => {
    const ids = new Set<number>();
    for (const doc of ungroupedDocuments) {
      if (doc.status === "completed") ids.add(doc.id);
    }
    for (const group of rootGroups) {
      for (const id of completedSubtreeDocIdsByGroupId.get(group.id) ?? []) {
        ids.add(id);
      }
    }
    return Array.from(ids);
  }, [completedSubtreeDocIdsByGroupId, rootGroups, ungroupedDocuments]);

  const visibleCompletedIdSet = useMemo(() => new Set(visibleCompletedIds), [visibleCompletedIds]);
  const hasVisibleSelection = useMemo(
    () => selected.some((id) => visibleCompletedIdSet.has(id)),
    [selected, visibleCompletedIdSet],
  );
  const visibleMatchCount = visibleCompletedIds.length;
  const searchAutoExpands = visibleMatchCount <= SEARCH_AUTO_EXPAND_LIMIT;

  // What is expanded on screen. With no search it is exactly the user's folders. During a search it
  // adds every matching folder (when under the auto-expand limit) and removes the ones the user
  // closed mid-search. Deriving this, instead of syncing state in an effect, means a widened query
  // that lifts the match count over the limit never renders the previous search's expansion.
  const expandedGroups = useMemo(() => {
    if (!normalizedSearch) return userExpandedGroups;
    const next = new Set(userExpandedGroups);
    if (searchAutoExpands) {
      for (const group of groups) {
        if (groupMatchesSearch(group.id)) next.add(group.id);
      }
    }
    for (const id of collapsedDuringSearch) next.delete(id);
    return next;
  }, [
    collapsedDuringSearch,
    groupMatchesSearch,
    groups,
    normalizedSearch,
    searchAutoExpands,
    userExpandedGroups,
  ]);

  const toggleGroupExpanded = useCallback(
    (groupId: number) => {
      const isExpanded = expandedGroups.has(groupId);
      setUserExpandedGroups((prev) => {
        const next = new Set(prev);
        if (isExpanded) {
          next.delete(groupId);
        } else {
          next.add(groupId);
        }
        return next;
      });
      if (normalizedSearch) {
        setCollapsedDuringSearch((prev) => {
          const next = new Set(prev);
          if (isExpanded) {
            next.add(groupId);
          } else {
            next.delete(groupId);
          }
          return next;
        });
      }
    },
    [expandedGroups, normalizedSearch],
  );

  const handleSave = async (asDefault = false) => {
    try {
      setSaving(true);
      const completedDocs = documents.filter((doc) => doc.status === "completed");
      const completedDocIds = completedDocs.map((doc) => doc.id);
      const finalSelection =
        asDefault || selected.length > 0 || completedDocs.length === 0
          ? [...selected]
          : completedDocIds;

      if (asDefault) {
        if (finalSelection.length === 0) {
          await deleteResearchDocumentDefault(municipalityId);
        } else {
          await updateResearchDocumentDefault(municipalityId, finalSelection);
        }
      }
      if (chatId !== -1) {
        await updateChatConfiguration(chatId, finalSelection);
      }
      onSelectionChange?.(finalSelection, completedDocs.length);
      setSelected(finalSelection);
      toast(
        asDefault
          ? finalSelection.length === 0
            ? {
                title: "Personal default cleared",
                description:
                  "This chat has no documents selected. New chats will start with every document selected.",
              }
            : {
                title: "Saved as your default",
                description: "New chats in this municipality will use this selection",
              }
          : {
              title: "Documents selected",
              description:
                finalSelection.length > 0
                  ? `${finalSelection.length} document${finalSelection.length === 1 ? "" : "s"} will be used for responses`
                  : "All documents will be used for responses",
            },
      );
      setOpen(false);
    } catch (error) {
      console.error("Error saving document selection:", error);
      toast({
        title: "Failed to save document selection",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          // Reset now rather than after the reopen refetch: the dialog renders once with the old
          // state before that fetch resolves, and a fully expanded tree is a very expensive render.
          resetSearchState();
          setUserExpandedGroups(new Set());
        }
      }}
    >
      <DialogTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-xs p-1 h-auto hover:bg-accent"
          title="Change which documents are used for responses"
        >
          <span>Select Documents</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="min-w-0 max-w-[min(32rem,calc(100vw-2rem))]">
        <DialogHeader>
          <DialogTitle>Select Documents</DialogTitle>
          <DialogDescription className="text-left">
            {promptForEmptySelection
              ? EMPTY_RESEARCH_SELECTION_PROMPT
              : "Select documents to limit answers to only use content from these sources."}
          </DialogDescription>
        </DialogHeader>
        <div className="min-w-0 space-y-3">
          <Input
            value={searchQuery}
            onChange={(event) => {
              const value = event.target.value;
              setSearchQuery(value);
              if (value === "") setCollapsedDuringSearch(new Set());
            }}
            placeholder="Search documents and folders..."
            aria-label="Search documents and folders"
            disabled={loading || documents.length === 0}
          />
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-8 w-8 border-2 border-primary rounded-full border-t-transparent"></div>
            </div>
          ) : (
            <div className="max-h-[60vh] min-w-0 overflow-hidden rounded-lg border">
              <div className="document-selector-dialog-scroll max-h-[60vh] min-w-0 max-w-full overflow-auto overflow-x-hidden pl-4 pr-1.5 pt-4 pb-6">
                {documents.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No documents available</p>
                ) : normalizedSearch &&
                  ungroupedDocuments.length === 0 &&
                  rootGroups.every((group) => !groupMatchesSearch(group.id)) ? (
                  <p className="text-center text-muted-foreground py-8">No documents match your search</p>
                ) : (
                  <div className="min-w-0 space-y-4">
                    {/* Lives inside the scroll region so it adds no height to the dialog; the footer already
                        sits at the card's edge when the list hits its max height. */}
                    {normalizedSearch && !searchAutoExpands && (
                      <p className="text-xs text-muted-foreground">
                        {visibleMatchCount.toLocaleString()} matching documents.{" "}
                        {rootGroups.length > 0
                          ? "Open a folder to see its matches, or keep typing to narrow the search."
                          : "Keep typing to narrow the search."}
                      </p>
                    )}
                    {rootGroups.some((group) => !normalizedSearch || groupMatchesSearch(group.id)) && (
                      <div className="min-w-0 space-y-3">
                        <h3 className="text-sm font-semibold">Document Groups</h3>
                        <DocumentGroupTreeRows
                          rootGroups={rootGroups}
                          depth={0}
                          documentsById={documentsById}
                          selected={selected}
                          selectedSet={selectedSet}
                          setSelected={setSelected}
                          expandedGroups={expandedGroups}
                          toggleGroupExpanded={toggleGroupExpanded}
                          childrenByParent={childrenByParent}
                          completedSubtreeDocIdsByGroupId={completedSubtreeDocIdsByGroupId}
                          normalizedSearch={normalizedSearch}
                          groupMatchesSearch={groupMatchesSearch}
                          searchHaystackById={searchHaystackById}
                          showSearchMatchedDescription
                        />
                      </div>
                    )}

                    {ungroupedDocuments.length > 0 && (
                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold">
                          {rootGroups.length > 0 ? "Other Documents" : "Available Documents"}
                        </h3>
                        <UngroupedDocumentRows
                          documents={ungroupedToRender.visible}
                          hiddenCount={ungroupedToRender.hiddenCount}
                          selectedSet={selectedSet}
                          normalizedSearch={normalizedSearch}
                          onCheckedChange={handleCheckedChange}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {/* sm:flex-col is required when save-as-default is on: DialogFooter's base sm:flex-row is not canceled by flex-col. */}
        <DialogFooter
          className={cn(
            "document-selector-footer w-full min-w-0 border-t pt-4",
            allowSaveAsDefault
              ? "flex flex-col gap-3 sm:flex-col sm:justify-start sm:space-x-0"
              : "gap-2 sm:justify-between sm:space-x-0",
          )}
        >
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelected((prev) => {
                  const next = new Set(prev);
                  for (const id of visibleCompletedIds) {
                    next.add(id);
                  }
                  return Array.from(next);
                });
              }}
              disabled={searchPending || visibleCompletedIds.length === 0}
            >
              Select All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (normalizedSearch) {
                  setSelected((prev) => prev.filter((id) => !visibleCompletedIdSet.has(id)));
                } else {
                  setSelected([]);
                }
              }}
              disabled={searchPending || (normalizedSearch ? !hasVisibleSelection : selected.length === 0)}
            >
              {normalizedSearch ? "Clear visible" : "Clear All"}
            </Button>
          </div>
          <div
            className={cn(
              "document-selector-footer-actions flex min-w-0",
              allowSaveAsDefault
                ? "w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-end"
                : "justify-end",
            )}
          >
            <div className="flex shrink-0 gap-2 self-end sm:self-auto">
              {allowSaveAsDefault ? (
                <Button variant="outline" size="sm" disabled={saving} onClick={() => void handleSave(true)}>
                  Save as default
                </Button>
              ) : null}
              <Button size="sm" disabled={saving} onClick={() => void handleSave()}>Save</Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export type MunicipalityDocumentPickerPanelProps = {
  municipalityId: number;
  selectedDocumentIds: number[];
  onSelectionChange: (ids: number[]) => void;
  maxSelection?: number;
};

/** Inline document tree for Plan Review code selection (no chat persistence). */
export function MunicipalityDocumentPickerPanel({
  municipalityId,
  selectedDocumentIds,
  onSelectionChange,
  maxSelection = 10,
}: MunicipalityDocumentPickerPanelProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [groups, setGroups] = useState<DocumentGroup[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<number[]>(selectedDocumentIds);
  const { toast } = useToast();

  useEffect(() => {
    setSelected(selectedDocumentIds);
  }, [selectedDocumentIds]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getDocumentsForMunicipality(municipalityId)
      .then((result) => {
        if (cancelled) return;
        let docs: Document[];
        let documentGroups: DocumentGroup[];
        if (Array.isArray(result)) {
          docs = result;
          documentGroups = [];
        } else {
          docs = result.documents || [];
          const rawGroups = (result.groups || []) as DocumentGroup[];
          const preById = indexGroupsById(rawGroups);
          const preChildren = buildChildrenByParentId(rawGroups);
          const preMemo = new Map<number, number[]>();
          documentGroups = rawGroups.filter((group) => {
            const subtree = subtreeDocumentIds(group.id, preById, preChildren, preMemo);
            return subtree.some((id: number) => {
              const doc = docs.find((d) => d.id === id);
              return doc !== undefined && doc.status === "completed";
            });
          });
        }
        setDocuments(docs);
        setGroups(documentGroups);
        setExpandedGroups(new Set());
      })
      .catch(() => {
        if (!cancelled) {
          toast({
            title: "Couldn't load code documents",
            description: "Please try again later.",
            variant: "destructive",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [municipalityId, toast]);

  const applySelection = (next: number[]) => {
    const capped = next.slice(0, maxSelection);
    if (next.length > maxSelection) {
      toast({
        title: `Select at most ${maxSelection} documents`,
        description: "Remove a selection before adding another code document.",
      });
    }
    setSelected(capped);
    onSelectionChange(capped);
  };

  const handleCheckedChange = (documentId: number, checked: boolean, status: string) => {
    if (status !== "completed") return;
    if (checked) {
      if (selected.includes(documentId)) return;
      if (selected.length >= maxSelection) {
        toast({
          title: `Select at most ${maxSelection} documents`,
          description: "Remove a selection before adding another code document.",
        });
        return;
      }
      applySelection([...selected, documentId]);
    } else {
      applySelection(selected.filter((id) => id !== documentId));
    }
  };

  const { groupsById, childrenByParent, rootGroups } = useMemo(() => {
    const byId = indexGroupsById(groups);
    const ch = buildChildrenByParentId(groups);
    return {
      groupsById: byId,
      childrenByParent: ch,
      rootGroups: ch.get(null) ?? [],
    };
  }, [groups]);

  const completedSubtreeDocIdsByGroupId = useMemo(() => {
    if (groups.length === 0) return new Map<number, number[]>();
    const subtreeMemo = new Map<number, number[]>();
    const completedIds = new Set(documents.filter((d) => d.status === "completed").map((d) => d.id));
    const out = new Map<number, number[]>();
    for (const g of groups) {
      const subtree = subtreeDocumentIds(g.id, groupsById, childrenByParent, subtreeMemo);
      out.set(
        g.id,
        subtree.filter((id) => completedIds.has(id)),
      );
    }
    return out;
  }, [documents, groups, groupsById, childrenByParent]);

  const groupedDocIds = useMemo(() => {
    if (groups.length === 0) return new Set<number>();
    return allGroupedDocumentIds(groups, groupsById, childrenByParent);
  }, [groups, groupsById, childrenByParent]);

  const toggleGroupExpanded = (groupId: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const documentsById = useMemo(() => new Map(documents.map((doc) => [doc.id, doc])), [documents]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const ungroupedDocuments = useMemo(
    () => documents.filter((doc) => !groupedDocIds.has(doc.id)),
    [documents, groupedDocIds],
  );

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (documents.filter((d) => d.status === "completed").length === 0) {
    return (
      <p className="py-4 text-sm text-muted-foreground">
        No code documents are set up for this city yet. Contact Ordinal support.
      </p>
    );
  }

  return (
    <div className="document-selector-dialog-scroll min-w-0 space-y-4">
      {rootGroups.length > 0 && (
        <DocumentGroupTreeRows
          rootGroups={rootGroups}
          depth={0}
          documentsById={documentsById}
          selected={selected}
          selectedSet={selectedSet}
          setSelected={(updater) => {
            const next = typeof updater === "function" ? updater(selected) : updater;
            applySelection(next);
          }}
          expandedGroups={expandedGroups}
          toggleGroupExpanded={toggleGroupExpanded}
          childrenByParent={childrenByParent}
          completedSubtreeDocIdsByGroupId={completedSubtreeDocIdsByGroupId}
        />
      )}
      {ungroupedDocuments.length > 0 && (
        <div className="space-y-2">
          {rootGroups.length > 0 ? (
            <h3 className="text-sm font-semibold">Other documents</h3>
          ) : null}
          {ungroupedDocuments.map((doc) => (
            <div
              key={doc.id}
              className="flex min-w-0 items-start gap-2 rounded-md py-1 hover:bg-muted/40"
            >
              <Checkbox
                id={`plan-review-doc-${doc.id}`}
                className="mt-0.5 shrink-0"
                checked={selected.includes(doc.id)}
                disabled={doc.status !== "completed"}
                onCheckedChange={(checked) => handleCheckedChange(doc.id, checked === true, doc.status)}
              />
              <Label
                htmlFor={`plan-review-doc-${doc.id}`}
                className={cn(
                  "block min-w-0 flex-1 cursor-pointer text-sm",
                  doc.status !== "completed" && "opacity-60",
                )}
              >
                <DocumentSelectorTitle name={doc.name} status={doc.status} />
              </Label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}